// src/components/Registros/forms/EquipamientoForm.jsx
import React, { useState } from 'react';
import { Container, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { FaArrowLeft, FaSave, FaTimes, FaLaptop } from 'react-icons/fa';
import Swal from 'sweetalert2';
import SelectMult from '../../SettingNot/SelectMult';
import { useNavigate } from 'react-router-dom';
import AdjuntosRegistro from '../AdjuntosRegistro';
import { crearDatos } from '../../../api/common';

const EquipamientoForm = ({ onClose, onSubmit, tipoRegistro, solicitante }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    solicitante: solicitante,
    titulo: '',
    fechaInicio: '',
    fechaFin: '',
    equipoSolicitado: '',
    tipoEquipo: '',
    especificacionesTecnicas: '',
    justificacion: '',
    descripcion: '',
    urgencia: 'media',
    responsablesAprobacion: [],
    adjuntos: []
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const responsables = [
    { id: '1', nombre: 'Juan Pérez', cargo: 'Jefe de Tecnología' },
    { id: '2', nombre: 'María García', cargo: 'Gerente Administrativo' }
  ];

  const tiposEquipo = [
    'Computador Portátil',
    'Computador de Escritorio',
    'Monitor',
    'Teclado',
    'Mouse',
    'Impresora',
    'Scanner',
    'Tablet',
    'Teléfono Móvil',
    'Auriculares',
    'Webcam',
    'Herramientas',
    'Otro'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleResponsableChange = (e) => {
    const { value } = e.target; // value es un array de IDs: ['id1', 'id2', ...]
    
    // Transformar el array de IDs a objetos con la estructura del modelo
    const responsablesFormateados = value?.map((usuarioId, index) => ({
      usuario: usuarioId,
      orden: index + 1,
      estado: 'pendiente'
    }));
    setFormData(prev => ({
      ...prev,
      responsablesAprobacion: responsablesFormateados
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Título se autogenera (ver handleSubmit).

    if (!formData.tipoEquipo) {
      newErrors.tipoEquipo = 'Debe seleccionar el tipo de equipo';
    }

    if (!formData.equipoSolicitado.trim()) {
      newErrors.equipoSolicitado = 'La descripción del equipo es requerida';
    }

    if (!formData.fechaInicio) {
      newErrors.fechaInicio = 'La fecha de inicio es requerida';
    }

    if (!formData.fechaFin) {
      newErrors.fechaFin = 'La fecha de fin es requerida';
    }

    if (formData.fechaInicio && formData.fechaFin) {
      if (new Date(formData.fechaFin) < new Date(formData.fechaInicio)) {
        newErrors.fechaFin = 'La fecha fin debe ser mayor o igual a la fecha inicio';
      }
    }

    if (!formData.justificacion.trim()) {
      newErrors.justificacion = 'La justificación es requerida';
    }

    if (formData.responsablesAprobacion.length === 0) {
      newErrors.responsablesAprobacion = 'Debe seleccionar al menos un responsable';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        icon: 'error',
        title: 'Errores en el formulario',
        text: 'Por favor, corrija los errores antes de continuar',
        confirmButtonColor: '#0d6efd'
      });
      return;
    }

    setLoading(true);

    try {
      const dataToSubmit = {
        ...formData,
        titulo: 'Solicitud de equipamiento',
        tipoSolicitud: 'equipamiento',
        causaInactividad: false
      };

      await crearDatos('Registro', dataToSubmit);

      await Swal.fire({
        icon: 'success',
        title: 'Solicitud Enviada',
        text: 'Tu solicitud de equipamiento ha sido registrada correctamente',
        confirmButtonColor: '#198754'
      });
      if (onSubmit) onSubmit(dataToSubmit);
      navigate('/Solicitudes');
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Hubo un error al enviar la solicitud. Por favor, intenta nuevamente.',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'Los datos ingresados se perderán',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, cancelar',
      cancelButtonText: 'No, continuar'
    }).then((result) => {
      if (result.isConfirmed) {
        onClose();
      }
    });
  };

  const getUrgenciaBadge = (urgencia) => {
    const badges = {
      baja: { color: '#6c757d', text: 'Baja' },
      media: { color: '#0d6efd', text: 'Media' },
      alta: { color: '#ffc107', text: 'Alta' },
      critica: { color: '#dc3545', text: 'Crítica' }
    };
    return badges[urgencia] || badges.media;
  };

  return (
    <Container fluid className="py-4" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      <div className="form-container">
        <div className="form-header" style={{ background: 'linear-gradient(135deg, #495057 0%, #343a40 100%)' }}>
          <div className="d-flex align-items-center">
            <Button variant="light" size="sm" onClick={handleCancel} className="me-3">
              <FaArrowLeft />
            </Button>
            <div>
              <h3 className="mb-0 fw-bold">Solicitud de Equipamiento</h3>
              <p className="mb-0 opacity-75">Complete el formulario para solicitar equipos o herramientas de trabajo</p>
            </div>
          </div>
        </div>

        <div className="form-body">
          <Alert variant="info" className="d-flex align-items-center">
            <FaLaptop className="me-2" size={20} />
            <div>
              <strong>Importante:</strong> Especifica claramente las características técnicas del equipo que necesitas 
              para garantizar que se adquiera el adecuado para tu trabajo.
            </div>
          </Alert>

          <Form onSubmit={handleSubmit}>
            {/* Información Básica */}
            <div className="form-section">
              <h5 className="form-section-title">Información Básica</h5>
              
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-bold" style={{display:'none'}}>Título (auto)</Form.Label>
                    <Form.Control
                      type="hidden"
                      name="titulo"
                      value={formData.titulo}
                      onChange={handleChange}
                      placeholder="Ej: Solicitud de laptop para desarrollo"
                      isInvalid={!!errors.titulo}
                      maxLength={100}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.titulo}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={8}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Tipo de Equipo *</Form.Label>
                    <Form.Select
                      name="tipoEquipo"
                      value={formData.tipoEquipo}
                      onChange={handleChange}
                      isInvalid={!!errors.tipoEquipo}
                    >
                      <option value="">Seleccione un tipo</option>
                      {tiposEquipo.map(tipo => (
                        <option key={tipo} value={tipo}>{tipo}</option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.tipoEquipo}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Urgencia *</Form.Label>
                    <Form.Select
                      name="urgencia"
                      value={formData.urgencia}
                      onChange={handleChange}
                    >
                      <option value="baja">Baja</option>
                      <option value="media">Media</option>
                      <option value="alta">Alta</option>
                      <option value="critica">Crítica</option>
                    </Form.Select>
                    <div className="mt-2">
                      <span 
                        className="badge"
                        style={{ 
                          backgroundColor: getUrgenciaBadge(formData.urgencia).color,
                          color: 'white'
                        }}
                      >
                        {getUrgenciaBadge(formData.urgencia).text}
                      </span>
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Descripción del Equipo *</Form.Label>
                    <Form.Control
                      type="text"
                      name="equipoSolicitado"
                      value={formData.equipoSolicitado}
                      onChange={handleChange}
                      placeholder="Ej: Laptop Dell XPS 15"
                      isInvalid={!!errors.equipoSolicitado}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.equipoSolicitado}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Especificaciones Técnicas */}
            <div className="form-section">
              <h5 className="form-section-title">Especificaciones Técnicas</h5>
              
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Especificaciones Detalladas</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  name="especificacionesTecnicas"
                  value={formData.especificacionesTecnicas}
                  onChange={handleChange}
                  placeholder="Especifica: procesador, memoria RAM, almacenamiento, pantalla, sistema operativo, u otras características técnicas relevantes..."
                  maxLength={800}
                />
                <Form.Text className="text-muted">
                  {formData.especificacionesTecnicas.length}/800 caracteres
                </Form.Text>
              </Form.Group>
            </div>

            {/* Periodo */}
            <div className="form-section">
              <h5 className="form-section-title">Fecha Requerida</h5>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Fecha Requerida (Inicio) *</Form.Label>
                    <Form.Control
                      type="date"
                      name="fechaInicio"
                      value={formData.fechaInicio}
                      onChange={handleChange}
                      isInvalid={!!errors.fechaInicio}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.fechaInicio}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Fecha Límite (Fin) *</Form.Label>
                    <Form.Control
                      type="date"
                      name="fechaFin"
                      value={formData.fechaFin}
                      onChange={handleChange}
                      isInvalid={!!errors.fechaFin}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.fechaFin}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Justificación */}
            <div className="form-section">
              <h5 className="form-section-title">Justificación de la Solicitud</h5>
              
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Justificación *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="justificacion"
                  value={formData.justificacion}
                  onChange={handleChange}
                  placeholder="Explica por qué necesitas este equipo, cómo mejorará tu productividad o es necesario para tu trabajo..."
                  isInvalid={!!errors.justificacion}
                  maxLength={500}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.justificacion}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  {formData.justificacion.length}/500 caracteres
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Observaciones Adicionales</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  placeholder="Información adicional relevante..."
                  maxLength={500}
                />
                <Form.Text className="text-muted">
                  {formData.descripcion.length}/500 caracteres
                </Form.Text>
              </Form.Group>
            </div>

            {/* Responsables */}
            <div className="form-section">
              <h5 className="form-section-title">Responsables de Aprobación</h5>
              
              <Form.Group>
                <Form.Label className="fw-bold">Seleccione los responsables que deben aprobar *</Form.Label>
                <SelectMult
                //dataDefault={itemData?.userAutorizados}
                handledata={handleResponsableChange}
                name='responsablesAprobacion'
                collection={"Usuarios"}
                field={"nombre"}
                baseFilter={[ // Body para la consuta aggregate de mongo para metrologos y que esten activos.
                        {
                            "$match": {
                            'status': 'activo'
                            } 
                        },
                        {
                            "$lookup": {
                            "from": "roles",
                            "localField": "role",
                            "foreignField": "_id",
                            "as": "result"
                            }
                        },
                        {
                            "$match": {
                            "result.nombre": { $ne: "Cliente" } 
                            }
                        }
                        ]}
                />
                {errors.responsablesAprobacion && (
                  <Alert variant="danger" className="mt-2 py-2">
                    {errors.responsablesAprobacion}
                  </Alert>
                )}
              </Form.Group>
            </div>

            <div className="form-section">
              <h5 className="form-section-title">Adjuntos</h5>
              <AdjuntosRegistro
                tipoSolicitud="equipamiento"
                adjuntos={formData.adjuntos}
                onChange={(arr) => setFormData(prev => ({ ...prev, adjuntos: arr }))}
                disabled={loading}
              />
            </div>

            {/* Botones */}
            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button 
                variant="secondary" 
                onClick={handleCancel}
                disabled={loading}
                className="px-4"
              >
                <FaTimes className="me-2" />
                Cancelar
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                disabled={loading}
                className="px-4"
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <FaSave className="me-2" />
                    Enviar Solicitud
                  </>
                )}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </Container>
  );
};

export default EquipamientoForm;